import apiClient, { prefetchApiCsrfToken } from "../common";
import type { BuyOrder } from "../types";

const JSON_API_MEDIA = "application/vnd.api+json";

export type CreateSupplyDocumentResult = {
  id: string;
  document_number: string;
};

function formatDateForApi(value: Date | string): string | undefined {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function supplierNameFromOrder(order: BuyOrder): string {
  const first = order.incoming_product_list?.[0];
  if (!first?.manufacturer) {
    return "";
  }
  return String(first.manufacturer);
}

function buildLineItems(order: BuyOrder) {
  const lines = order.incoming_product_list ?? [];
  return lines
    .filter((line) => String(line.product_code ?? "").trim())
    .map((line) => {
      const bulks = Number(line.bulks) || 0;
      const unitsPerBulk = Number(line.units_per_bulk) || 0;
      const totalUnits =
        line.total_units != null
          ? Number(line.total_units)
          : bulks > 0 && unitsPerBulk > 0
            ? bulks * unitsPerBulk
            : undefined;
      const item: Record<string, string | number> = {
        "product-code": String(line.product_code).trim(),
      };
      if (line.description) {
        item.description = String(line.description);
      }
      if (bulks > 0) {
        item.bulks = bulks;
      }
      if (unitsPerBulk > 0) {
        item["units-per-bulk"] = unitsPerBulk;
      }
      if (totalUnits != null && !Number.isNaN(totalUnits) && totalUnits > 0) {
        item["total-units"] = totalUnits;
      }
      return item;
    });
}

function buildCreatePayload(order: BuyOrder, simulationId: string) {
  const expectedReceipt = formatDateForApi(order.ata);
  const atawDate = order.ataw != null ? formatDateForApi(order.ataw) : undefined;
  const attributes: Record<string, string | unknown> = {
    "document-origin": "simulated",
    "document-type": "PO",
    "document-number": String(order.number ?? "").trim(),
    "document-status": "draft",
    simulation: String(simulationId),
  };
  if (expectedReceipt) {
    attributes["expected-receipt-date"] = expectedReceipt;
  }
  if (atawDate) {
    attributes["ataw-date"] = atawDate;
  }
  const supplierName = supplierNameFromOrder(order);
  if (supplierName) {
    attributes["supplier-name"] = supplierName;
  }
  const lineItems = buildLineItems(order);
  if (lineItems.length > 0) {
    attributes["line-items"] = lineItems;
  }

  return {
    data: {
      type: "supply-documents",
      attributes,
    },
  };
}

export type CreateSupplyDocumentOptions = {
  /** Exportación a Monday: upsert por número y pone document-status en real. */
  forMondayExport?: boolean;
};

export async function apiCreateSupplyDocument(
  order: BuyOrder,
  simulationId: string,
  options?: CreateSupplyDocumentOptions,
): Promise<CreateSupplyDocumentResult> {
  await prefetchApiCsrfToken();
  const res = await apiClient.post(
    "supply-documents/",
    buildCreatePayload(order, simulationId),
    {
      params: options?.forMondayExport ? { for_monday_export: true } : {},
      headers: {
        Accept: JSON_API_MEDIA,
        "Content-Type": JSON_API_MEDIA,
      },
    },
  );
  const row = res.data?.data ?? res.data;
  const attrs = row?.attributes ?? {};
  return {
    id: String(row?.id ?? ""),
    document_number: String(
      attrs["document-number"] ?? attrs.document_number ?? order.number ?? "",
    ),
  };
}

export async function apiCreateSupplyDocumentsForOrders(
  orders: BuyOrder[],
  simulationId: string,
  options?: CreateSupplyDocumentOptions,
): Promise<CreateSupplyDocumentResult[]> {
  if (orders.length === 0) {
    return [];
  }
  const results: CreateSupplyDocumentResult[] = [];
  for (const order of orders) {
    results.push(await apiCreateSupplyDocument(order, simulationId, options));
  }
  return results;
}
